/**
 * Shortcut grammar of the interaction kit (plan §4.2). Combos are lower case and platform-neutral:
 *
 *   'mod+k'        ⌘K on macOS, Ctrl+K elsewhere
 *   'mod+shift+z'  modifiers joined with '+', the key last
 *   'g h'          a sequence: g, then h within one second
 *   '?'            a printable character; Shift is optional for punctuation ('?' is Shift+/ on many layouts)
 *   'shift+x'      Shift is required for letters ('x' and 'shift+x' are different commands)
 *   'alt+arrowup', 'delete', 'backspace', 'space', 'enter', 'escape', 'f2', 'home', 'pagedown', …
 *
 * Parsing and matching go through tinykeys (MIT); the platform decides what `mod` means, and labels
 * use `formatShortcut` of `@dataroom/ui` (⌘⇧Z on macOS, Ctrl+Shift+Z elsewhere).
 */
import { matchKeybindingPress, parseKeybinding } from 'tinykeys';

export type Platform = 'mac' | 'windows' | 'linux' | 'other';

type NavigatorLike = {
  platform?: string;
  userAgent?: string;
  userAgentData?: { platform?: string };
};

/** Platform from `navigator.userAgentData.platform`, else `navigator.platform`, else the user agent. */
export function detectPlatform(nav: NavigatorLike | undefined = globalThis.navigator): Platform {
  if (!nav) return 'other';
  const raw =
    `${nav.userAgentData?.platform ?? ''} ${nav.platform ?? ''} ${nav.userAgent ?? ''}`.toLowerCase();
  if (/mac|iphone|ipad|ipod/.test(raw)) return 'mac';
  if (/win/.test(raw)) return 'windows';
  if (/linux|x11|cros|android/.test(raw)) return 'linux';
  return 'other';
}

let platformOverride: Platform | null = null;

/** The platform shortcuts are matched and labelled for (tests may override it). */
export function currentPlatform(): Platform {
  return platformOverride ?? detectPlatform();
}

/** Tests and the shortcut gallery: pretend to run on `platform` (null = detect again). */
export function setPlatform(platform: Platform | null): void {
  platformOverride = platform;
}

export const isMac = (platform: Platform = currentPlatform()) => platform === 'mac';

/** A combo, or one combo per platform (`{ mac: 'mod+shift+z', other: 'ctrl+y' }`). */
export type ShortcutSpec = string | { mac?: string; windows?: string; linux?: string; other?: string };

const MODIFIERS = new Set(['mod', 'ctrl', 'control', 'alt', 'option', 'shift', 'meta', 'cmd']);
const KEY_ALIASES: Record<string, string> = {
  esc: 'escape',
  del: 'delete',
  return: 'enter',
  up: 'arrowup',
  down: 'arrowdown',
  left: 'arrowleft',
  right: 'arrowright',
  pgup: 'pageup',
  pgdn: 'pagedown',
  ' ': 'space',
  spacebar: 'space',
  plus: '=',
};

export type Stroke = {
  /** Normalised key name: a character ('k', '?', '[') or a named key ('arrowup', 'space', 'f2'). */
  key: string;
  mod: boolean;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
};

/** Parse one combo ('mod+shift+z', 'g h') into its strokes. Throws on an empty or malformed combo. */
export function parseCombo(combo: string): Stroke[] {
  const presses = combo.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (presses.length === 0) throw new Error(`Empty shortcut "${combo}"`);
  return presses.map((press) => {
    // '+' as a key is written '=' (or 'plus'); split on '+' that follows a character.
    const parts = press.split('+').filter((p, i, all) => p !== '' || i === all.length - 1);
    const rawKey = parts.pop();
    if (!rawKey) throw new Error(`Shortcut "${combo}" has no key`);
    const key = KEY_ALIASES[rawKey] ?? rawKey;
    if (MODIFIERS.has(key)) throw new Error(`Shortcut "${combo}" ends with a modifier`);
    const stroke: Stroke = { key, mod: false, ctrl: false, alt: false, shift: false, meta: false };
    for (const m of parts) {
      if (m === 'mod') stroke.mod = true;
      else if (m === 'ctrl' || m === 'control') stroke.ctrl = true;
      else if (m === 'alt' || m === 'option') stroke.alt = true;
      else if (m === 'shift') stroke.shift = true;
      else if (m === 'meta' || m === 'cmd') stroke.meta = true;
      else throw new Error(`Unknown modifier "${m}" in shortcut "${combo}"`);
    }
    return stroke;
  });
}

/** True for a printable key that types text ('a', '7', '?', '['), false for named keys. */
export function isPrintableKey(key: string): boolean {
  return [...key].length === 1;
}

/** A single stroke without ⌘/Ctrl/⌥ (Shift alone does not make a chord): the text and grid guards apply. */
export function isSingleKey(combo: string): boolean {
  const strokes = parseCombo(combo);
  return strokes.every((s) => !s.mod && !s.ctrl && !s.alt && !s.meta);
}

/** Canonical form for comparing bindings on one platform: 'mod' resolved, modifiers sorted. */
export function canonicalCombo(combo: string, platform: Platform = currentPlatform()): string {
  return parseCombo(combo)
    .map((s) => {
      const meta = s.meta || (s.mod && platform === 'mac');
      const ctrl = s.ctrl || (s.mod && platform !== 'mac');
      const mods = [ctrl && 'ctrl', s.alt && 'alt', s.shift && 'shift', meta && 'meta'].filter(Boolean);
      return [...mods, s.key].join('+');
    })
    .join(' ');
}

/** The combo of a spec on a platform (undefined when the spec has none for it). */
export function resolveShortcut(
  spec: ShortcutSpec,
  platform: Platform = currentPlatform(),
): string | undefined {
  if (typeof spec === 'string') return spec;
  return spec[platform] ?? spec.other;
}

function namedKeyForTinykeys(key: string): string {
  switch (key) {
    case 'space':
      return 'Space'; // matched against event.code (a literal space would split the sequence)
    case 'escape':
      return 'Escape';
    case 'enter':
      return 'Enter';
    case 'delete':
      return 'Delete';
    case 'backspace':
      return 'Backspace';
    case 'tab':
      return 'Tab';
    case 'arrowup':
      return 'ArrowUp';
    case 'arrowdown':
      return 'ArrowDown';
    case 'arrowleft':
      return 'ArrowLeft';
    case 'arrowright':
      return 'ArrowRight';
    case 'home':
      return 'Home';
    case 'end':
      return 'End';
    case 'pageup':
      return 'PageUp';
    case 'pagedown':
      return 'PageDown';
    default:
      return /^f\d{1,2}$/.test(key) ? key.toUpperCase() : key;
  }
}

/** Physical keys of US-layout punctuation (⌥] types ‘ on macOS, so ⌥⌘] matches the key code too). */
const PUNCTUATION_CODES: Record<string, string> = {
  '[': 'BracketLeft',
  ']': 'BracketRight',
  '-': 'Minus',
  '=': 'Equal',
  ';': 'Semicolon',
  ',': 'Comma',
  '.': 'Period',
  '/': 'Slash',
  '\\': 'Backslash',
  '`': 'Backquote',
};

// tinykeys compiles keys with the `v` flag: only syntax characters (and '/') may be escaped.
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');

/** tinykeys keybinding string of one stroke on a platform. */
export function toTinykeys(stroke: Stroke, platform: Platform): string {
  const mods: string[] = [];
  if (stroke.ctrl || (stroke.mod && platform !== 'mac')) mods.push('Control');
  if (stroke.meta || (stroke.mod && platform === 'mac')) mods.push('Meta');
  if (stroke.alt) mods.push('Alt');
  if (stroke.shift) mods.push('Shift');
  let key = namedKeyForTinykeys(stroke.key);
  const printable = isPrintableKey(stroke.key);
  const letterOrDigit = /^[a-z0-9]$/.test(stroke.key);
  // Punctuation is typed with Shift (or AltGr) on many layouts: Shift is optional unless required.
  if (printable && !letterOrDigit && !stroke.shift) mods.push('[Shift]');
  // ⌥ changes the character on macOS (⌥M types µ) and ⇧ changes a digit (⇧1 types !): match the
  // physical key as well.
  const digit = /^\d$/.test(stroke.key);
  if (printable && letterOrDigit && (stroke.alt || (stroke.shift && digit)))
    key = `(${escapeRegex(stroke.key)}|${digit ? 'Digit' : 'Key'}${stroke.key.toUpperCase()})`;
  else if (printable && !letterOrDigit)
    key =
      stroke.alt && PUNCTUATION_CODES[stroke.key]
        ? `(${escapeRegex(stroke.key)}|${PUNCTUATION_CODES[stroke.key]})`
        : `(${escapeRegex(stroke.key)})`;
  return [...mods, key].join('+');
}

type Press = ReturnType<typeof parseKeybinding>[number];
const pressCache = new Map<string, Press[]>();

/** Compiled tinykeys presses of a combo on a platform (cached). */
export function compileCombo(combo: string, platform: Platform = currentPlatform()): Press[] {
  const cacheKey = `${platform}|${combo}`;
  let presses = pressCache.get(cacheKey);
  if (!presses) {
    presses = parseCombo(combo).map((s) => parseKeybinding(toTinykeys(s, platform))[0]!);
    pressCache.set(cacheKey, presses);
  }
  return presses;
}

/** Does `event` match stroke `index` of `combo`? */
export function matchesStroke(
  event: KeyboardEvent,
  combo: string,
  index = 0,
  platform: Platform = currentPlatform(),
): boolean {
  const press = compileCombo(combo, platform)[index];
  if (!press || !event.key) return false;
  return matchKeybindingPress(event, press);
}

/** Number of strokes in a combo (2 for 'g h'). */
export function strokeCount(combo: string): number {
  return parseCombo(combo).length;
}

/** Modifier keys pressed alone never trigger anything. */
export function isModifierKey(event: KeyboardEvent): boolean {
  return ['Shift', 'Control', 'Alt', 'Meta', 'AltGraph', 'CapsLock', 'Fn', 'OS'].includes(event.key);
}

const NON_TEXT_INPUTS = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

/** Focus on a target that types text: inputs, textareas, selects and contenteditable editors. */
export function isTextTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).closest !== 'function') return false;
  const el = target as HTMLElement;
  if (el instanceof HTMLInputElement) return !NON_TEXT_INPUTS.has(el.type);
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el.isContentEditable) return true;
  // CodeMirror and tiptap render contenteditable content, so the attribute covers them.
  return Boolean(
    el.closest(
      '[contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"], [role="textbox"]',
    ),
  );
}

/** Focus on a grid cell cursor (`role="grid"` / `treegrid`): letters edit the cell there. */
export function isGridTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).closest !== 'function') return false;
  return Boolean((target as Element).closest('[role="grid"], [role="treegrid"]'));
}
